import * as Sentry from '@sentry/capacitor';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileContentTypeEnum } from './constants/session';
import { ApiError, type PatientSessionDetails } from '~/api/generated';

const { isNativePlatform } = usePlatform();

const recordingContentType = isNativePlatform ? FileContentTypeEnum.Aac : FileContentTypeEnum.Webm;

const downloadFile = (url: string | undefined, name: string = ''): void => {
  if (!url) return;

  const link = document.createElement('a');

  link.setAttribute('href', url);
  link.setAttribute('download', name);
  link.setAttribute('target', '_blank');
  document.body.appendChild(link);
  link.click();

  // To make this work on Firefox we need to wait
  // a little while before removing it.
  setTimeout(() => {
    document.body.removeChild(link);
  }, 0);
};

const base64ToBlob = (base64: string, type: string) => {
  const byteCharacters = atob(base64);

  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);

  return new Blob([byteArray], { type });
};

const getPhotosFromStorage = async (session: PatientSessionDetails['id']) => {
  try {
    const path = getPhotosDirectory(session);
    const { files } = await Filesystem.readdir({ path, directory: Directory.Documents });

    if (!files.length) return [];

    return await Promise.all(
      files.map(async ({ name }) => {
        const sessionPath = `${path}/${name}`;

        const { data } = await Filesystem.readFile({ path: sessionPath, directory: Directory.Documents });

        const file = base64ToBlob(data as string, FileContentTypeEnum.Jpg);

        return {
          file,
          original_file_name: name,
          size: file.size,
          session
        };
      })
    );
  } catch (error) {
    Sentry.captureException(error);
    return [];
  }
};

// Get all recordings for a specific session from storage
// Should be used only on web platform
const getRecordingsFromStorage = async (sessionId: PatientSessionDetails['id']): Promise<Array<Blob>> => {
  try {
    const dirPath = getSessionsDirectory(sessionId);
    const { files } = await Filesystem.readdir({
      path: dirPath,
      directory: Directory.Documents
    });

    return await Promise.all(
      files.map(async ({ name }) => {
        const recordingPath = `${dirPath}/${name}`;
        const { data } = await Filesystem.readFile({
          path: recordingPath,
          directory: Directory.Documents
        });

        return data as Blob;
      })
    );
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.log(error);
    return [];
  }
};

const getUnprocessedPhotos = async (session: PatientSessionDetails['id']) => {
  const appName = await getRootDirectory();

  try {
    const { files: dataDirFiles } = await Filesystem.readdir({
      directory: Directory.Documents,
      path: appName
    });

    if (!dataDirFiles.some(({ name }) => name === 'photos')) {
      return [];
    }

    const { files: sessionDirFiles } = await Filesystem.readdir({
      directory: Directory.Documents,
      path: getPhotosDirectory()
    });

    if (!sessionDirFiles.some(({ name }) => name === session.toString())) {
      return [];
    }

    const { files } = await Filesystem.readdir({
      directory: Directory.Documents,
      path: getPhotosDirectory(session)
    });

    if (!files.length) return [];

    return (
      await Promise.all(
        files.map((file) =>
          Filesystem.readFile({
            path: getPhotosDirectory(`${session}/${file.name}`),
            directory: Directory.Documents
          })
        )
      )
    ).map(({ data }, index) => ({ data: data as string, path: files[index].name }));
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.log(error);
    return [];
  }
};

const checkUnprocessedSession = async (sessionId: PatientSessionDetails['id']) => {
  try {
    const sessionDir = getSessionsDirectory(sessionId);

    const sessionExists = await Filesystem.stat({
      path: sessionDir,
      directory: Directory.Documents
    })
      .then(() => true)
      .catch(() => false);

    if (!sessionExists) return false;

    const { files } = await Filesystem.readdir({
      path: sessionDir,
      directory: Directory.Documents
    });

    return files?.length > 0;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);

    return false;
  }
};

const deleteSessionFiles = async (sessionId: PatientSessionDetails['id']) => {
  try {
    const sessionDirExists = await Filesystem.stat({
      path: getSessionsDirectory(sessionId),
      directory: Directory.Documents
    })
      .then(() => true)
      .catch(() => false);

    if (sessionDirExists) {
      await Filesystem.rmdir({
        path: getSessionsDirectory(sessionId),
        directory: Directory.Documents,
        recursive: true
      });
    }

    const photosDirExists = await Filesystem.stat({
      path: getPhotosDirectory(sessionId),
      directory: Directory.Documents
    })
      .then(() => true)
      .catch(() => false);

    if (photosDirExists) {
      await Filesystem.rmdir({
        path: getPhotosDirectory(sessionId),
        directory: Directory.Documents,
        recursive: true
      });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
  }
};

class UploadError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = 'UploadError';
  }
}

const handleProcessRecordingError = (error: unknown, message: string) => {
  const { extractApiError } = useApiUtils();

  Sentry.captureException(error);

  extractApiError(error as ApiError, message);
  throw new UploadError();
};

export {
  downloadFile,
  base64ToBlob,
  getPhotosFromStorage,
  getRecordingsFromStorage,
  recordingContentType,
  getUnprocessedPhotos,
  checkUnprocessedSession,
  deleteSessionFiles,
  UploadError,
  handleProcessRecordingError
};
